import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import type { ActionDescriptor } from '@finance/contracts';
import { ActionRenderer } from '../experience/ActionRenderer.tsx';
import { resolveActions } from '../../runtime/actions/action-resolution';
import { useEvaluationContext } from '../../runtime/conditions/use-evaluation-context';

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ActionDescriptor[];
  actionNode?: React.ReactNode;
  onAction?: (action: ActionDescriptor) => void;
}

export function PageHeader({ title, subtitle, actions, actionNode, onAction }: PageHeaderProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const evalCtx = useEvaluationContext();
  const gap = theme.financeLayout?.page?.sectionGap?.comfortable ?? 2;
  const resolvedActions = useMemo(
    () =>
      resolveActions(actions ?? [], {
        ctx: evalCtx,
      }),
    [actions, evalCtx],
  );

  return (
    <Box component="header" sx={{ mb: gap }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h2" component="h1" sx={{ mb: subtitle ? 1 : 0 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        {actionNode ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>{actionNode}</Box>
        ) : actions && actions.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
            {resolvedActions.map((action) => (
              <ActionRenderer key={action.action.id} action={action} onAction={(next) => onAction?.(next.action)} />
            ))}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
